import React, { useEffect } from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { ERouteLinks } from 'models/route';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslations } from 'hooks/useTranslations';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EPermission } from 'models/permissions';
import { renderDataColor } from 'components/molecules/Table/helpers';
import { Chips } from 'components/atoms/Chips';

interface IFirearmTransactionTableCard {
  discardableFirearmTransactionId?: string | null;
  discardDisabled: boolean;
  data: ITableDataSource;
  handleDelete: (transactionId: string) => void;
}

const FirearmTransactionTableCard = ({
  data,
  discardableFirearmTransactionId,
  discardDisabled,
  handleDelete,
}: IFirearmTransactionTableCard) => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { longDateFormat, getProperties } = useGlobalProperty();

  useEffect(() => {
    getProperties();
  }, []);

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: colors.gray100,
      }}
    >
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(
            'registeredFirearms.firearmsDetails.transactions.accordion.transactionType.column',
          )}
        </Typography>
        <Chips text={data.transactionType} variant={renderDataColor(data).stateField} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(
            'registeredFirearms.firearmsDetails.transactions.accordion.transactionDateAndTime.column',
          )}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(formatDate(data.date, longDateFormat))}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.firearmsDetails.transactions.accordion.createdDate.column')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(formatDate(data.createdDate, longDateFormat))}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.firearmsDetails.transactions.accordion.owner.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.owner)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.firearmsDetails.transactions.accordion.keeper.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.keeper)}</Typography>
      </Grid>
      <Grid
        size={{ xs: 12 }}
        container
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          pt: { xs: 0, sm: 1 },
          m: 2,
        }}
        spacing={1}
      >
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {`${t('actions.general.label')}: `}
          </Typography>
        </Grid>
        {data.detailsVisible && (
          <Grid size={{ xs: 12, sm: 3 }}>
            <Link to={ERouteLinks.ViewTransaction.replace(':id', data.id as string)}>
              <Button
                label={t(
                  'registeredFirearms.firearmsDetails.transactions.accordion.details.link',
                )}
                variant={EButtonVariants.outlined}
                size="small"
                fullWidth
              />
            </Link>
          </Grid>
        )}
        {discardableFirearmTransactionId === (data.id as string) && !discardDisabled && (
          <Grid size={{ xs: 12, sm: 3 }}>
            <Button
              label={t(
                'registeredFirearms.firearmsDetails.transactions.accordion.remove.link',
              )}
              variant={EButtonVariants.outlined}
              permission={EPermission.DISCARD_TRANSACTION}
              onClick={() => handleDelete(data.id as string)}
              size="small"
              fullWidth
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export { FirearmTransactionTableCard };
